import { RefElement } from 'widgets/toolbox/RefElement';

/**
 * @description Base InputSelect extending
 * @param InputSelect Base widget for extending
 * @returns Input Select class
 */
function InputSelectExtClassCreator(InputSelect: import('widgets/forms/InputSelect').TInputSelect) {
    /**
     * @category widgets
     * @subcategory forms
     * @class InputSelectExt
     * @augments InputSelect
     * @classdesc Select input implementation. Represents input `password` element together with widget-related HTML markup.
     * HTML structure assembled on backend and injected into resulted html markup by using `formElement` component
     * and dynamic forms configuration JSON.
     * @property {string} data-widget - Widget name `inputSelect`
     * @property {boolean} [first-default] - Select first option even if other option marked as selected
     */
    class InputSelectExt extends InputSelect {
        prefs() {
            return {
                openCssClass: 'm-open',
                blurOnChange: false,
                ...super.prefs()
            };
        }

        /**
         * @description Handles select click
         * @listens dom:click
         */
        handleClick() {
            this.ref('self').toggleClass(this.prefs().openCssClass);
        }

        /**
         * @description onClick alias for handleClick - required for sign-in-to-buy form compatibility
         * @listens dom:click
         */
        onClick() {
            this.handleClick();
        }

        /**
         * @description InputSelect on change handler
         * @listens dom:change
         * @param el source of event
         * @param event event instance if DOM event
         */
        onChange(el: RefElement, event: Event | undefined) {
            super.onChange(el, event);

            if (this.prefs().blurOnChange) {
                setTimeout(() => {
                    this.blur();
                }, 0);
            }
        }

        /**
         * @description Handles blur event
         * @listens dom:blur
         */
        handleBlur() {
            this.ref('self').removeClass(this.prefs().openCssClass);
        }

        /**
         * @description Sets new value on select from selected option; new value is found by text
         * @param newVal new value
         * @param silently validate after change
         */
        setValueByText(newVal: string = this.initValue, silently?: boolean) {
            let val = newVal || '';

            const field = this.ref('field').get<HTMLSelectElement>();

            if (field) {
                const option = Array.from(field.options).find(elem => elem.text === val);

                if (!option) {
                    val = this.initValue || '';
                } else {
                    val = option.value;
                }
            }

            super.setValue(val, silently);
        }
    }

    return InputSelectExt;
}

export type TInputSelectExt = ReturnType<typeof InputSelectExtClassCreator>;

export type TInputSelectExtInstance = InstanceType<TInputSelectExt>;

export default InputSelectExtClassCreator;
